import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { VersionAnswers } from "./VersionAnswers";
import type { Template } from "./types";
import { useFiles } from "@/api/hooks/files";
import { useQuestionnaireVersion, useQuestionnaireVersions, type VersionSummary } from "@/api/hooks/questionnaire";
import { Disclosure } from "@/components/Disclosure";
import { EmptyState } from "@/components/EmptyState";
import { ErrorNotice } from "@/components/ErrorNotice";
import { PageLoading } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { useProjectParams } from "@/features/projects/projectContext";
import { formatDate } from "@/lib/dates";
import { Markdown } from "@/lib/markdown";

const STATUS_VARIANT = { submitted: "success", draft: "warning", none: "grey" } as const;

function hasFileQuestions(body: Template | undefined): boolean {
  return Boolean(body?.sections.some((s) => s.questions.some((q) => q.type === "file_upload")));
}

function PanelBody({ version, isLatest, notes }: { version: number; isLatest: boolean; notes: string }) {
  const { t } = useTranslation();
  const { org, project } = useProjectParams();
  const detail = useQuestionnaireVersion(org, project, version);
  const body = detail.data?.template?.body;
  const files = useFiles(org, project, { enabled: hasFileQuestions(body) });
  return (
    <div className="flex flex-col gap-4">
      {notes ? (
        <div className="flex flex-col gap-1 rounded-md bg-surface-muted p-4">
          <p className="text-sm font-semibold text-heading">{t("questionnaire.whatChanged", { version })}</p>
          <Markdown source={notes} className="text-sm" />
        </div>
      ) : null}
      {isLatest ? (
        <Link to={`/${org}/${project}/questionnaire`} className="inline-flex min-h-11 items-center gap-2 self-start font-medium">
          {t("questionnaire.continueAnswering")}
          <ArrowRight aria-hidden="true" className="h-4 w-4" />
        </Link>
      ) : null}
      {detail.isPending ? <PageLoading /> : null}
      {detail.isError ? <ErrorNotice error={detail.error} /> : null}
      {body ? (
        <VersionAnswers
          template={body}
          answers={detail.data?.response?.answers ?? {}}
          fileName={(id) => files.data?.find((f) => f.id === id)?.title}
        />
      ) : null}
    </div>
  );
}

function PanelHeader({ v, latest }: { v: VersionSummary; latest: number }) {
  const { t } = useTranslation();
  const status = v.status === "submitted" || v.status === "draft" ? v.status : "none";
  return (
    <span className="flex flex-col gap-1">
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-heading font-semibold text-heading">{t("questionnaire.versionLabel", { version: v.version })}</span>
        <Badge variant={STATUS_VARIANT[status]}>{t(`questionnaire.versionStatus.${status}`)}</Badge>
        {v.version !== latest ? (
          <span className="text-sm font-normal text-fg-muted">{t("questionnaire.replacedBy", { version: latest })}</span>
        ) : null}
      </span>
      <span className="text-sm font-normal text-fg-muted">{t("questionnaire.publishedOn", { date: formatDate(v.published_at) })}</span>
    </span>
  );
}

/** Earlier versions: one collapsible panel per version with the person's own answers. */
export function QuestionnaireHistoryPage() {
  const { t } = useTranslation();
  const { org, project } = useProjectParams();
  const [params] = useSearchParams();
  const selected = params.get("version") ? Number(params.get("version")) : null;
  const versions = useQuestionnaireVersions(org, project);
  const [open, setOpen] = useState<Set<number>>(() => new Set(selected !== null ? [selected] : []));
  const selectedRef = useRef<HTMLDivElement>(null);
  const loaded = Boolean(versions.data);

  useEffect(() => {
    if (loaded) selectedRef.current?.scrollIntoView?.({ block: "start" });
  }, [loaded]);

  const toggle = (v: number, on: boolean) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (on) next.add(v);
      else next.delete(v);
      return next;
    });

  const latest = versions.data?.[0]?.version ?? 0;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Link to=".." relative="path" className="inline-flex min-h-11 items-center gap-2 text-sm font-medium">
          <ArrowLeft aria-hidden="true" className="h-4 w-4" />
          {t("questionnaire.backToCurrent")}
        </Link>
        <h2>{t("questionnaire.historyTitle")}</h2>
        <p className="text-fg-muted">{t("questionnaire.historyIntro")}</p>
      </div>
      {versions.isPending ? <PageLoading /> : null}
      {versions.isError ? <ErrorNotice error={versions.error} /> : null}
      {versions.data && versions.data.length === 0 ? <EmptyState title={t("questionnaire.emptyTitle")} /> : null}
      {versions.data && versions.data.length > 0 ? (
        <div className="flex flex-col gap-4">
          {versions.data.map((v) => (
            <Disclosure
              key={v.version}
              ref={v.version === selected ? selectedRef : undefined}
              open={open.has(v.version)}
              onOpenChange={(on) => toggle(v.version, on)}
              header={<PanelHeader v={v} latest={latest} />}
            >
              <PanelBody version={v.version} isLatest={v.version === latest} notes={v.notes} />
            </Disclosure>
          ))}
        </div>
      ) : null}
    </div>
  );
}
